import { useEffect } from 'react';

import type { User } from '@enterprise/api';
import { broadcastOnce, TypedBroadcastChannel } from '@enterprise/core';

import { useApplicationStore } from '@hooks/application/application-store.hook';

import type { AuthBroadcastMessage } from './types/auth-broadcast-message.type';

import { useNavigateToLogin } from './navigate-to-login.hook';

const CHANNEL_NAME = 'datastream:auth';

/**
 * Broadcasts a login event to all other tabs so they can hydrate the user session
 * without requiring a full login flow.
 *
 * Intended to be called directly (not as a hook) from the session callback page
 * after a successful authentication handshake.
 */
export const broadcastAuthLogin = (user: User): void => {
  broadcastOnce<AuthBroadcastMessage>(CHANNEL_NAME, { type: 'login', user });
};

/**
 * Broadcasts a logout event to all other tabs so they can clear their session
 * and redirect to the login page.
 *
 * Intended to be called directly (not as a hook) from the logout page after the
 * OAuth logout handshake completes.
 */
export const broadcastAuthLogout = (): void => {
  broadcastOnce<AuthBroadcastMessage>(CHANNEL_NAME, { type: 'logout' });
};

export const useCrossTabAuth = () => {
  const setUser = useApplicationStore(({ setUser }) => {
    return setUser;
  });
  const { navigateToLogin } = useNavigateToLogin();

  useEffect(() => {
    const channel = new TypedBroadcastChannel<AuthBroadcastMessage>(CHANNEL_NAME, (message) => {
      switch (message.type) {
        case 'login': {
          setUser(message.user);
          break;
        }

        case 'logout': {
          setUser(undefined);
          navigateToLogin();
          break;
        }
      }
    });

    return () => {
      channel.close();
    };
  }, [navigateToLogin, setUser]);

  return { broadcastLogout: broadcastAuthLogout };
};
